import { buildQueryString } from '../../lib/query-params.ts';
import { apiClient } from '../client.ts';
import type {
  Server,
  ServerGroup,
  CreateServerParams,
  CreateServerResult,
  UpdateServerParams,
  CreateServerGroupParams,
  UpdateServerGroupParams,
  TestConnectionResult,
  TestConnectionRawParams,
  TestConnectionRawResult,
  AttachSSHParams,
  HealthCheckResult,
  ExecuteCommandParams,
  ExecuteCommandResult,
  ServerHandoff,
  ServerService,
  ServiceScanRequestResult,
  CreateServerServiceParams,
  RuntimeInventory,
  PaginatedResponse,
  ProbeEnrollmentBootstrap,
  HardeningStatus,
  HardeningRun,
  CreateHardeningRunParams,
  CreateInventoryMutationParams,
  InventoryMutation,
  ServerTagDefinition,
  CreateServerTagDefinitionParams,
  UpdateServerTagDefinitionParams,
  BulkServerTagsParams,
  BulkServerTagsResult,
  ServerShare,
  CreateServerShareParams,
  UserResolveResult,
} from '../types.ts';

// --- Servers ---

export async function listServers(params?: {
  page?: number;
  pageSize?: number;
  teamId?: string;
  groupId?: string;
  status?: string;
  search?: string;
  /** Comma-separated or single tag; also matches region */
  tag?: string;
  excludeTag?: string;
  sortBy?: string;
  sortDirection?: string;
  /** Light field projection support (passed through; backend may ignore for now) */
  fields?: string[];
}): Promise<PaginatedResponse<Server>> {
  return apiClient.get<PaginatedResponse<Server>>(`/servers${buildQueryString(params)}`);
}

export async function listServerTagDefinitions(params?: {
  teamId?: string;
  category?: string;
}): Promise<ServerTagDefinition[]> {
  // Backend: { success, data, totalCount }. Client may return bare array or { data, totalCount }.
  const raw = await apiClient.get<ServerTagDefinition[] | { data?: ServerTagDefinition[]; totalCount?: number }>(
    `/servers/tag-definitions${buildQueryString(params)}`,
  );
  if (Array.isArray(raw)) return raw;
  return Array.isArray(raw?.data) ? raw.data : [];
}

export async function createServerTagDefinition(
  params: CreateServerTagDefinitionParams,
): Promise<ServerTagDefinition> {
  return apiClient.post<ServerTagDefinition>('/servers/tag-definitions', params);
}

export async function updateServerTagDefinition(
  tagId: string,
  params: UpdateServerTagDefinitionParams,
): Promise<ServerTagDefinition> {
  return apiClient.patch<ServerTagDefinition>(`/servers/tag-definitions/${tagId}`, params);
}

export async function deleteServerTagDefinition(tagId: string): Promise<void> {
  await apiClient.delete(`/servers/tag-definitions/${tagId}`);
}

export async function bulkServerTags(params: BulkServerTagsParams): Promise<BulkServerTagsResult> {
  return apiClient.post<BulkServerTagsResult>('/servers/tags/bulk', params);
}

export async function getServer(serverId: string): Promise<Server> {
  return apiClient.get<Server>(`/servers/${serverId}`);
}

// --- Server shares (owner manage) ---

export async function listServerShares(
  serverId: string,
  params?: { includeInactive?: boolean },
): Promise<ServerShare[]> {
  const qs = buildQueryString(
    params?.includeInactive ? { includeInactive: '1' } : undefined,
  );
  const raw = await apiClient.get<ServerShare[] | { data?: ServerShare[] }>(
    `/servers/${serverId}/shares${qs}`,
  );
  if (Array.isArray(raw)) return raw;
  return Array.isArray(raw?.data) ? raw.data : [];
}

export async function createServerShare(
  serverId: string,
  params: CreateServerShareParams,
): Promise<ServerShare> {
  return apiClient.post<ServerShare>(`/servers/${serverId}/shares`, params);
}

export async function revokeServerShare(serverId: string, shareId: string): Promise<void> {
  await apiClient.delete(`/servers/${serverId}/shares/${shareId}`);
}

/** Exact username/email resolve for share grantee (no email in response). */
export async function resolveUser(q: string): Promise<UserResolveResult> {
  return apiClient.get<UserResolveResult>(`/users/resolve${buildQueryString({ q })}`);
}

export async function createServer(params: CreateServerParams): Promise<CreateServerResult> {
  return apiClient.post<CreateServerResult>('/servers', params);
}

export async function regenerateProbeEnrollment(serverId: string): Promise<ProbeEnrollmentBootstrap> {
  return apiClient.post<ProbeEnrollmentBootstrap>(`/servers/${serverId}/probe/enrollment/regenerate`);
}

export async function updateServer(serverId: string, params: UpdateServerParams): Promise<Server> {
  return apiClient.patch<Server>(`/servers/${serverId}`, params);
}

export async function deleteServer(serverId: string): Promise<void> {
  await apiClient.delete(`/servers/${serverId}`);
}

export async function testServerConnection(serverId: string): Promise<TestConnectionResult> {
  return apiClient.post<TestConnectionResult>(`/servers/${serverId}/test`);
}

export async function testConnectionRaw(params: TestConnectionRawParams): Promise<TestConnectionRawResult> {
  return apiClient.post<TestConnectionRawResult>('/servers/test-connection', params);
}

export async function attachServerSSH(serverId: string, params: AttachSSHParams): Promise<Server> {
  return apiClient.post<Server>(`/servers/${serverId}/attach-ssh`, params);
}

export async function detachServerSSH(serverId: string): Promise<Server> {
  return apiClient.post<Server>(`/servers/${serverId}/detach-ssh`);
}

export async function healthCheckServer(serverId: string): Promise<HealthCheckResult> {
  return apiClient.post<HealthCheckResult>(`/servers/${serverId}/check`);
}

export async function executeCommand(
  serverId: string,
  params: ExecuteCommandParams,
): Promise<ExecuteCommandResult> {
  return apiClient.post<ExecuteCommandResult>(`/servers/${serverId}/execute`, params);
}

export async function getServerHandoff(serverId: string): Promise<ServerHandoff> {
  return apiClient.get<ServerHandoff>(`/servers/${serverId}/server-handoff?format=json`);
}

export async function getRuntimeInventory(serverId: string): Promise<RuntimeInventory> {
  return apiClient.get<RuntimeInventory>(`/servers/${serverId}/runtime-inventory`);
}

// --- Hardening ---

export async function getHardeningStatus(serverId: string): Promise<HardeningStatus> {
  return apiClient.get<HardeningStatus>(`/servers/${serverId}/hardening/status`);
}

export async function listHardeningRuns(serverId: string): Promise<HardeningRun[]> {
  return apiClient.get<HardeningRun[]>(`/servers/${serverId}/hardening/runs`);
}

export async function getHardeningRun(serverId: string, runId: string): Promise<HardeningRun> {
  return apiClient.get<HardeningRun>(`/servers/${serverId}/hardening/runs/${runId}`);
}

export async function createHardeningRun(
  serverId: string,
  params: CreateHardeningRunParams,
): Promise<HardeningRun> {
  return apiClient.post<HardeningRun>(`/servers/${serverId}/hardening/runs`, params);
}

export async function rollbackHardeningRun(serverId: string, runId: string): Promise<unknown> {
  return apiClient.post(`/servers/${serverId}/hardening/runs/${runId}/rollback`);
}

export async function createInventoryMutation(
  serverId: string,
  params: CreateInventoryMutationParams,
): Promise<InventoryMutation> {
  return apiClient.post<InventoryMutation>(`/servers/${serverId}/inventory-mutations`, params);
}

// --- Server Groups ---

export async function listServerGroups(params?: {
  teamId?: string;
}): Promise<{ data: ServerGroup[]; totalCount: number }> {
  // Backend returns { success, data: ServerGroup[], totalCount } without meta.
  // apiClient unwraps to the array only — normalize so callers always get { data, totalCount }.
  const raw = await apiClient.get<ServerGroup[] | { data?: ServerGroup[]; totalCount?: number }>(
    `/servers/groups${buildQueryString(params)}`,
  );
  if (Array.isArray(raw)) {
    return { data: raw, totalCount: raw.length };
  }
  const data = Array.isArray(raw?.data) ? raw.data : [];
  return {
    data,
    totalCount: typeof raw?.totalCount === 'number' ? raw.totalCount : data.length,
  };
}

export async function createServerGroup(params: CreateServerGroupParams): Promise<ServerGroup> {
  return apiClient.post<ServerGroup>('/servers/groups', params);
}

export async function updateServerGroup(
  groupId: string,
  params: UpdateServerGroupParams,
): Promise<ServerGroup> {
  return apiClient.patch<ServerGroup>(`/servers/groups/${groupId}`, params);
}

export async function deleteServerGroup(groupId: string): Promise<void> {
  await apiClient.delete(`/servers/groups/${groupId}`);
}

// --- Server Services (Service Discovery) ---

export async function listServerServices(serverId: string): Promise<ServerService[]> {
  return apiClient.get<ServerService[]>(`/servers/${serverId}/services`);
}

export async function createServerService(
  serverId: string,
  params: CreateServerServiceParams,
): Promise<ServerService> {
  return apiClient.post<ServerService>(`/servers/${serverId}/services`, params);
}

export async function requestServiceScan(serverId: string): Promise<ServiceScanRequestResult> {
  return apiClient.post<ServiceScanRequestResult>(`/servers/${serverId}/services/scan`);
}

export async function updateServerServiceStatus(
  serverId: string,
  serviceId: string,
  status: 'registered' | 'excluded',
): Promise<ServerService> {
  return apiClient.patch<ServerService>(`/servers/${serverId}/services/${serviceId}`, { status });
}
